import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ProspectsToApproveProps,
  PendingProspect,
  GetPendingProspectsForCampaignResponse,
  RejectProspectResponse,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import ItemList from '../../../common/ItemList';
import Prospect from './Prospect';
import { useDependencyUpdates } from '../../../../providers';

import { ReactComponent as ThumbsDownIcon } from '../../../../icons/thumbs-down.svg';

import './prospects-to-approve.scss';

interface ProspectsToApproveComponentProps extends ProspectsToApproveProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const ProspectsToApprove: React.FC<ProspectsToApproveComponentProps> = ({
  campaignId,
  prospectIds,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const [prospects, setProspects] = useState<PendingProspect[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProspectId, setSelectedProspectId] = useState<string | null>(
    null,
  );
  const { getAccessTokenSilently } = useAuth0();
  const listWrapperRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState<number | null>(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    hasNext: false,
    hasPrev: false,
  });

  const fetchProspects = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        const params: any = { page, limit: 10 };

        // Add prospectIds filter if provided
        if (prospectIds && prospectIds.length > 0) {
          params.prospectIds = prospectIds.join(',');
        }

        const response = await api.get<GetPendingProspectsForCampaignResponse>(
          `/prospect/campaign/${campaignId}/pending`,
          {
            params,
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setProspects(response.data.prospects);
          setPagination(response.data.pagination);
        } else {
          setError(response.data.error || 'Failed to load prospects');
        }
      } catch (err) {
        console.error('Error fetching pending prospects:', err);
        setError('Failed to load prospects');
      } finally {
        setLoading(false);
      }
    },
    [campaignId, prospectIds, getAccessTokenSilently],
  );

  useEffect(() => {
    fetchProspects(1);
  }, [fetchProspects]);

  // Listen for campaign updates (when prospects are added/removed)
  const { registerCallback } = useDependencyUpdates();
  useEffect(() => {
    const unregister = registerCallback('campaigns', campaignId, () => {
      console.log('Campaign pending prospects updated, reloading...');
      fetchProspects(pagination.page);
    });

    return () => unregister();
  }, [campaignId, registerCallback, fetchProspects, pagination.page]);

  // Capture list height when prospects load
  useEffect(() => {
    if (listWrapperRef.current && !loading && prospects.length > 0) {
      setListHeight(listWrapperRef.current.offsetHeight);
    }
  }, [loading, prospects]);

  const handleProspectClick = (prospectId: string) => {
    // Only allow clicking if we don't have a threadId
    if (!threadId) {
      // Capture current height before switching
      if (listWrapperRef.current) {
        setListHeight(listWrapperRef.current.offsetHeight);
      }
      setSelectedProspectId(prospectId);
    }
  };

  const handleBackClick = () => {
    setSelectedProspectId(null);
  };

  const goToNextPage = () => {
    if (pagination.hasNext) {
      fetchProspects(pagination.page + 1);
    }
  };

  const goToPrevPage = () => {
    if (pagination.hasPrev) {
      fetchProspects(pagination.page - 1);
    }
  };

  const handleRejectProspect = async (prospectId: string) => {
    // Store the prospect for potential rollback
    const rejectedProspect = prospects.find(
      (p) => p.id.toString() === prospectId,
    );

    if (!rejectedProspect) return;

    // Optimistically remove the prospect from the list
    setProspects((prev) => prev.filter((p) => p.id.toString() !== prospectId));

    // Update pagination count
    setPagination((prev) => ({
      ...prev,
      total: prev.total - 1,
    }));

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post<RejectProspectResponse>(
        `/prospect/${prospectId}/reject`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to reject prospect');
      }
    } catch (error) {
      console.error('Error rejecting prospect:', error);

      // Rollback: re-add the prospect to the list
      setProspects((prev) => [...prev, rejectedProspect]);

      // Rollback pagination count
      setPagination((prev) => ({
        ...prev,
        total: prev.total + 1,
      }));

      // TODO: Show error toast/notification
      alert('Failed to reject prospect. Please try again.');
    }
  };

  // If a prospect is selected and we don't have a threadId, show the Prospect component
  if (selectedProspectId && !threadId) {
    return (
      <div
        className='prospects-to-approve-wrapper-with-prospect'
        style={listHeight ? { minHeight: `${listHeight}px` } : undefined}
      >
        <div className='back-button-wrapper'>
          <button onClick={handleBackClick} className='back-button'>
            <span>← Back to list</span>
          </button>
        </div>
        <Prospect
          prospectId={selectedProspectId}
          threadId={threadId}
          updateThreadViewVisualDescription={updateThreadViewVisualDescription}
        />
      </div>
    );
  }

  // Transform prospects to items for ItemList
  const items = prospects.map((prospect) => ({
    id: prospect.id.toString(),
    imagePlaceholder: prospect.knownOrgName || '',
    title:
      prospect.fullName ||
      `${prospect.firstName || ''} ${prospect.lastName || ''}`.trim() ||
      'Unknown',
    subDescription: prospect.jobTitle || undefined,
    description: prospect.knownOrgName || undefined,
    img: prospect.knownOrgLogoUrl || undefined,
    actions: [
      <button
        className='prospect-reject-button'
        key='delete-prospect-button'
        onClick={(e) => {
          e.stopPropagation();
          handleRejectProspect(prospect.id.toString());
        }}
      >
        <ThumbsDownIcon />
      </button>,
    ],
  }));

  return (
    <div className='prospects-to-approve-wrapper' ref={listWrapperRef}>
      <div className='prospects-list'>
        <ItemList
          items={items}
          loading={loading}
          error={!!error}
          emptyHeader='No pending prospects'
          emptyText='All prospects have been reviewed for this campaign.'
          showPagination={true}
          nextVisible={pagination.hasNext}
          prevVisible={pagination.hasPrev}
          onNext={goToNextPage}
          onPrev={goToPrevPage}
          onItemClick={threadId ? undefined : handleProspectClick}
        />
      </div>
    </div>
  );
};

export default ProspectsToApprove;
